import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ANCHOR_READ_PERMISSION,
  REFERENCE_CATEGORY_LABELS,
  REFERENCE_LINK_TARGET_LABELS,
  REFERENCE_TAG_MAX,
  REFERENCE_TAGS_PER_REFERENCE_MAX,
  ReferenceCategory,
  ReferenceLinkTargetType,
  referenceTagKey,
  type ReferenceCategory as Category,
  type ReferenceGroupView,
  type ReferenceLinkTargetOption,
  type ReferenceLinkTargetType as TargetType,
} from '@scoutaround/script-breakdown-shared';

import { api } from '../../lib/api.js';
import { useCan } from '../../lib/permissions.js';
import { Icon, type IconName } from '../Icon.js';
import { errorText, useDebounced } from './hooks.js';

/**
 * THE FIELDS A REFERENCE IS ORGANISED BY — category, groups, tags and links —
 * as controls the add drawer (local, applied at creation) and the detail panel
 * (saved as they change) both use, so the two cannot disagree about what a tag
 * is or which items can be linked.
 */

export const TARGET_ICON: Readonly<Record<TargetType, IconName>> = Object.freeze({
  SCENE: 'scenes',
  REQUIREMENT: 'pin',
  PLACE: 'map',
  ELEMENT: 'model',
});

const TARGET_NOUN: Readonly<Record<TargetType, string>> = Object.freeze({
  SCENE: 'a scene',
  REQUIREMENT: 'a script location',
  PLACE: 'a place',
  ELEMENT: 'a production element',
});

export function CategorySelect({
  value,
  onChange,
  disabled = false,
  testId,
  id,
}: {
  value: Category | null;
  onChange: (next: Category | null) => void;
  disabled?: boolean;
  testId: string;
  id?: string;
}) {
  return (
    <select
      id={id}
      className="ref-select"
      value={value ?? ''}
      disabled={disabled}
      data-testid={testId}
      onChange={(event) => {
        const parsed = ReferenceCategory.safeParse(event.target.value);
        onChange(parsed.success ? parsed.data : null);
      }}
    >
      <option value="">Not set</option>
      {ReferenceCategory.options.map((c) => (
        <option key={c} value={c}>
          {REFERENCE_CATEGORY_LABELS[c]}
        </option>
      ))}
    </select>
  );
}

/** The groups a reference is in, each removable, and a list of the others to add it to. */
export function GroupChooser({
  groups,
  value,
  onChange,
  disabled = false,
  testId,
  labelId,
}: {
  groups: readonly ReferenceGroupView[];
  value: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  testId: string;
  labelId?: string;
}) {
  const chosen = value
    .map((id) => groups.find((g) => g.id === id) ?? null)
    .filter((g): g is ReferenceGroupView => g !== null);
  const others = groups.filter((g) => !value.includes(g.id));

  return (
    <div className="ref-chooser" data-testid={testId}>
      {chosen.length === 0 && others.length === 0 ? (
        <p className="ref-quiet">No groups yet. Create one under Groups / Themes.</p>
      ) : null}
      {chosen.length > 0 ? (
        <ul className="ref-chip-list" aria-labelledby={labelId}>
          {chosen.map((g) => (
            <li key={g.id} className="ref-chip" data-tone="group" data-testid={`${testId}-chip-${g.id}`}>
              <span>{g.name}</span>
              {disabled ? null : (
                <button
                  type="button"
                  className="ref-chip-x"
                  aria-label={`Take it out of ${g.name}`}
                  data-testid={`${testId}-remove-${g.id}`}
                  onClick={() => {
                    onChange(value.filter((id) => id !== g.id));
                  }}
                >
                  <Icon name="close" size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {!disabled && others.length > 0 ? (
        <select
          className="ref-select"
          value=""
          aria-label="Add to a group"
          data-testid={`${testId}-add`}
          onChange={(event) => {
            if (event.target.value !== '') onChange([...value, event.target.value]);
          }}
        >
          <option value="">Add to a group…</option>
          {others.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      ) : null}
    </div>
  );
}

/**
 * Tags, deduplicated as the server keys them (`referenceTagKey`): "Natural
 * light" and "natural  LIGHT" are one tag, so the second is never sent.
 */
export function TagEditor({
  tags,
  suggestions,
  onChange,
  disabled = false,
  testId,
}: {
  tags: readonly string[];
  suggestions: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  testId: string;
}) {
  const [draft, setDraft] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const listId = useId();
  const inputId = useId();
  const keys = new Set(tags.map(referenceTagKey));
  const offered = suggestions.filter((s) => !keys.has(referenceTagKey(s))).slice(0, 30);

  const add = () => {
    const label = draft.trim().replace(/\s+/g, ' ');
    if (label === '') return;
    if (label.length > REFERENCE_TAG_MAX) {
      setNotice(`A tag is at most ${String(REFERENCE_TAG_MAX)} characters.`);
      return;
    }
    if (keys.has(referenceTagKey(label))) {
      setNotice(`Already tagged “${label}”.`);
      setDraft('');
      return;
    }
    if (tags.length >= REFERENCE_TAGS_PER_REFERENCE_MAX) {
      setNotice(`A reference carries at most ${String(REFERENCE_TAGS_PER_REFERENCE_MAX)} tags.`);
      return;
    }
    setNotice(null);
    setDraft('');
    onChange([...tags, label]);
  };

  return (
    <div className="ref-tags" data-testid={testId}>
      {tags.length > 0 ? (
        <ul className="ref-chip-list" aria-label="Tags">
          {tags.map((t) => (
            <li key={referenceTagKey(t)} className="ref-chip" data-testid={`${testId}-chip-${referenceTagKey(t)}`}>
              <span>{t}</span>
              {disabled ? null : (
                <button
                  type="button"
                  className="ref-chip-x"
                  aria-label={`Remove tag ${t}`}
                  data-testid={`${testId}-remove-${referenceTagKey(t)}`}
                  onClick={() => {
                    setNotice(null);
                    onChange(tags.filter((x) => referenceTagKey(x) !== referenceTagKey(t)));
                  }}
                >
                  <Icon name="close" size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : disabled ? (
        <p className="ref-quiet">No tags.</p>
      ) : null}
      {disabled ? null : (
        <div className="ref-inline-add">
          <label className="sr-only" htmlFor={inputId}>
            Add a tag
          </label>
          <input
            id={inputId}
            className="ref-input"
            value={draft}
            placeholder="Add a tag"
            maxLength={REFERENCE_TAG_MAX + 10}
            list={offered.length > 0 ? listId : undefined}
            data-testid={`${testId}-input`}
            onChange={(event) => {
              setDraft(event.target.value);
              setNotice(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ',') {
                event.preventDefault();
                add();
              }
            }}
          />
          {offered.length > 0 ? (
            <datalist id={listId}>
              {offered.map((s) => (
                <option key={referenceTagKey(s)} value={s} />
              ))}
            </datalist>
          ) : null}
          <button type="button" className="ctl" data-testid={`${testId}-add`} disabled={draft.trim() === ''} onClick={add}>
            Add
          </button>
        </div>
      )}
      {notice !== null ? (
        <p className="ref-quiet" role="status" data-testid={`${testId}-notice`}>
          {notice}
        </p>
      ) : null}
    </div>
  );
}

export interface EditableLink {
  /** The link row's id once it exists; the target's own key before. */
  readonly key: string;
  readonly targetType: TargetType;
  readonly targetId: string;
  readonly label: string;
  readonly removed?: boolean;
}

/**
 * Links grouped by what they point at, each group with its own searchable
 * picker. A type the person may not read is not offered (the hint; the server
 * refuses it anyway), and a link to an item that has since left the production
 * stays, marked removed, under the label it was made with.
 */
export function LinksEditor({
  projectId,
  links,
  editable,
  busy = false,
  onAdd,
  onRemove,
  testId,
}: {
  projectId: string;
  links: readonly EditableLink[];
  editable: boolean;
  busy?: boolean;
  onAdd: (option: ReferenceLinkTargetOption) => void;
  onRemove: (link: EditableLink) => void;
  testId: string;
}) {
  return (
    <div className="ref-links" data-testid={testId}>
      {ReferenceLinkTargetType.options.map((type) => (
        <LinkGroup
          key={type}
          projectId={projectId}
          type={type}
          links={links.filter((l) => l.targetType === type)}
          editable={editable}
          busy={busy}
          onAdd={onAdd}
          onRemove={onRemove}
          testId={`${testId}-${type}`}
        />
      ))}
    </div>
  );
}

function LinkGroup({
  projectId,
  type,
  links,
  editable,
  busy,
  onAdd,
  onRemove,
  testId,
}: {
  projectId: string;
  type: TargetType;
  links: readonly EditableLink[];
  editable: boolean;
  busy: boolean;
  onAdd: (option: ReferenceLinkTargetOption) => void;
  onRemove: (link: EditableLink) => void;
  testId: string;
}) {
  const needed = ANCHOR_READ_PERMISSION[type];
  const canRead = useCan(needed ?? 'PROJECT_READ');
  const [open, setOpen] = useState(false);
  if (!canRead) return null;
  if (links.length === 0 && !editable) return null;

  return (
    <div className="ref-link-group" data-testid={testId}>
      <p className="ref-link-group-head">
        <Icon name={TARGET_ICON[type]} size={14} />
        <span>{REFERENCE_LINK_TARGET_LABELS[type]}</span>
      </p>
      {links.length > 0 ? (
        <ul className="ref-chip-list">
          {links.map((l) => (
            <li
              key={l.key}
              className="ref-chip"
              data-tone="link"
              data-removed={l.removed === true ? '1' : undefined}
              data-testid={`${testId}-link-${l.targetId}`}
            >
              <span title={l.removed === true ? `${l.label} — no longer in the production` : l.label}>{l.label}</span>
              {l.removed === true ? <span className="ref-chip-note">removed</span> : null}
              {editable ? (
                <button
                  type="button"
                  className="ref-chip-x"
                  aria-label={`Unlink ${l.label}`}
                  disabled={busy}
                  data-testid={`${testId}-unlink-${l.targetId}`}
                  onClick={() => {
                    onRemove(l);
                  }}
                >
                  <Icon name="close" size={12} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {editable ? (
        open ? (
          <TargetSearch
            projectId={projectId}
            type={type}
            taken={new Set(links.filter((l) => l.removed !== true).map((l) => l.targetId))}
            busy={busy}
            onPick={(option) => {
              onAdd(option);
              setOpen(false);
            }}
            onClose={() => {
              setOpen(false);
            }}
            testId={`${testId}-picker`}
          />
        ) : (
          <button
            type="button"
            className="ref-text-btn"
            data-testid={`${testId}-open`}
            onClick={() => {
              setOpen(true);
            }}
          >
            <Icon name="plus" size={13} />
            {`Link ${TARGET_NOUN[type]}`}
          </button>
        )
      ) : null}
    </div>
  );
}

function TargetSearch({
  projectId,
  type,
  taken,
  busy,
  onPick,
  onClose,
  testId,
}: {
  projectId: string;
  type: TargetType;
  taken: ReadonlySet<string>;
  busy: boolean;
  onPick: (option: ReferenceLinkTargetOption) => void;
  onClose: () => void;
  testId: string;
}) {
  const [q, setQ] = useState('');
  const settled = useDebounced(q, 250);
  const inputId = useId();
  const options = useQuery({
    queryKey: ['references', projectId, 'link-targets', type, settled],
    queryFn: () => api.referenceLinkTargets(projectId, type, settled),
    staleTime: 30_000,
  });

  return (
    <div
      className="ref-target-search"
      data-testid={testId}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="ref-inline-add">
        <label className="sr-only" htmlFor={inputId}>
          {`Find ${TARGET_NOUN[type]} to link`}
        </label>
        <input
          id={inputId}
          className="ref-input"
          value={q}
          autoFocus
          placeholder={`Find ${TARGET_NOUN[type]}`}
          data-testid={`${testId}-input`}
          onChange={(event) => {
            setQ(event.target.value);
          }}
        />
        <button type="button" className="ctl" data-testid={`${testId}-close`} onClick={onClose}>
          Done
        </button>
      </div>
      {options.isPending ? <p className="ref-quiet">Searching…</p> : null}
      {options.isError ? (
        <p className="ref-quiet" role="alert">
          {errorText(options.error, 'The list could not be loaded.')}
        </p>
      ) : null}
      {options.data && options.data.length === 0 ? (
        <p className="ref-quiet" data-testid={`${testId}-empty`}>
          {settled.trim() === '' ? `Nothing in this production to link yet.` : `Nothing matches “${settled.trim()}”.`}
        </p>
      ) : null}
      {options.data && options.data.length > 0 ? (
        <ul className="ref-target-options" aria-label={`${REFERENCE_LINK_TARGET_LABELS[type]} options`}>
          {options.data.map((o) => {
            const already = taken.has(o.targetId);
            return (
              <li key={o.targetId}>
                <button
                  type="button"
                  className="ref-target-option"
                  disabled={already || busy}
                  data-testid={`${testId}-option-${o.targetId}`}
                  onClick={() => {
                    onPick(o);
                  }}
                >
                  <span className="ref-target-label">{o.label}</span>
                  {o.sublabel ? <span className="ref-target-sub">{o.sublabel}</span> : null}
                  {already ? <span className="ref-target-sub">Linked</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
